<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  isVoiceCallEnabled,
  getVoiceCallProvider,
  getInboxIconByType,
  INBOX_TYPES,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
} from 'dashboard/components-next/message/constants';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useCallsStore } from 'dashboard/stores/calls';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { getBrowserVoiceSession } from 'customDashboard/lib/voice/voiceSessionRegistry';
import {
  ensureVoiceConversation,
  findVoiceConversationId,
} from 'customDashboard/lib/voice/ensureVoiceConversation';
import { wavoipOutboundBlockedReasonKey } from 'customDashboard/lib/wavoip/wavoipOutboundPreflight';
import {
  unlockWavoipOutboundRingback,
  stopWavoipOutboundRingback,
} from 'customDashboard/lib/wavoip/wavoipOutboundRingback';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  // When set, the WhatsApp call continues in this conversation (matching the
  // header button) instead of looking up the contact's most recent one.
  conversationId: { type: [String, Number], default: null },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const route = useRoute();
const router = useRouter();
const store = useStore();

const { t } = useI18n();

const dialogRef = ref(null);

const callsStore = useCallsStore();
const inboxesList = useMapGetter('inboxes/getInboxes');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');

const voiceInboxes = computed(() =>
  (inboxesList.value || []).filter(isVoiceCallEnabled)
);
const hasVoiceInboxes = computed(() => voiceInboxes.value.length > 0);

const shouldRender = computed(() => hasVoiceInboxes.value && !!props.phone);

// Twilio sets isInitiatingCall via Vuex; WhatsApp/Wavoip are tracked locally.
const twilioIsInitiating = computed(
  () => contactsUiFlags.value?.isInitiatingCall || false
);
const isStartingCall = ref(false);
const isInitiatingCall = computed(
  () => isStartingCall.value || twilioIsInitiating.value
);

// Mirror the conversation-header button: block a new call whenever any provider
// call is already active or ringing, otherwise starting a WhatsApp call here
// would leave a still-live Twilio (or other) session with no visible control.
const isCallButtonDisabled = computed(
  () =>
    callsStore.hasActiveCall ||
    callsStore.hasIncomingCall ||
    isInitiatingCall.value
);

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

const whatsappCallSession = useWhatsappCallSession();

// WhatsApp /initiate is conversation-scoped (unlike Twilio's contact-scoped path).
// Pass inboxId so the BE applies the filter before the 20-row cap — without it,
// contacts whose latest WhatsApp conversation falls outside the 20 most recent
// across all inboxes would be treated as having no conversation.
const findConversationInInbox = inboxId =>
  findVoiceConversationId(props.contactId, inboxId);

const startWhatsappCall = async (inboxId, conversationIdHint) => {
  // WhatsApp /initiate is conversation-scoped, so we must hand it a
  // conversation. Use the caller's hint when given (in-conversation flow);
  // otherwise pick the most recent one in the inbox.
  const conversationId =
    conversationIdHint || (await findConversationInInbox(inboxId));
  if (!conversationId) {
    useAlert(t('CONTACT_PANEL.CALL_FAILED'));
    return;
  }

  const response =
    await whatsappCallSession.initiateOutboundCall(conversationId);
  // The composable returns { status: 'locked' } when an init is already in
  // flight or a call is already active; treat that as a soft no-op rather than
  // claiming success.
  if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
  if (!response?.id) {
    // Permission template path returns no call id. Mirror the header button and
    // surface whether the request was just sent or is already pending instead of
    // claiming the call started. The permission message lands in the
    // conversation, so still navigate there.
    const messageKey =
      response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
        ? 'CONTACT_PANEL.WHATSAPP_CALL_PERMISSION_PENDING'
        : 'CONTACT_PANEL.WHATSAPP_CALL_PERMISSION_REQUESTED';
    useAlert(t(messageKey));
    navigateToConversation(conversationId);
    return;
  }

  // Stay non-active until the connect cable event arrives — flipping to active
  // here would start the duration timer before the contact picks up.
  callsStore.addCall({
    callSid: response.call_id,
    callId: response.id,
    conversationId,
    inboxId,
    callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    provider: VOICE_CALL_PROVIDERS.WHATSAPP,
  });

  useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
  navigateToConversation(conversationId);
};

const startWavoipCall = async (inboxId, conversationIdHint) => {
  const session = getBrowserVoiceSession(VOICE_CALL_PROVIDERS.WAVOIP);
  if (!session) return;

  // FORK: muted unlock before conversation lookup / connect awaits.
  unlockWavoipOutboundRingback();

  let conversationId = conversationIdHint;
  if (!conversationId) {
    conversationId = await findConversationInInbox(inboxId);
  }
  if (!conversationId) {
    try {
      conversationId = await ensureVoiceConversation({
        contactId: props.contactId,
        inboxId,
        phone: props.phone,
        channelType: INBOX_TYPES.WAVOIP,
      });
    } catch {
      stopWavoipOutboundRingback();
      useAlert(t('CONTACT_PANEL.VOICE_CONVERSATION_REQUIRED'));
      return;
    }
  }
  if (!conversationId) {
    stopWavoipOutboundRingback();
    useAlert(t('CONTACT_PANEL.VOICE_CONVERSATION_REQUIRED'));
    return;
  }

  // initiateOutboundCall below connects (and translates connection/device
  // errors) on its own — this is just an eager warm-up so the SDK offer
  // listener is attached ASAP. A failure here shouldn't block the actual
  // attempt or shadow its more specific error message.
  try {
    await session.connectForInbox?.(inboxId);
  } catch (warmupError) {
    // eslint-disable-next-line no-console
    console.debug('[Wavoip] call button warm-up connect failed', warmupError);
  }
  try {
    const response = await session.initiateOutboundCall(conversationId, {
      inboxId,
      toPhone: props.phone,
    });
    if (response?.status === 'locked' || !response?.call_id) {
      stopWavoipOutboundRingback();
      if (!response?.call_id && response?.status !== 'locked') {
        useAlert(t('CONTACT_PANEL.CALL_FAILED'));
        navigateToConversation(conversationId);
      }
      return;
    }
  } catch (error) {
    stopWavoipOutboundRingback();
    useAlert(error?.message || t('CONTACT_PANEL.CALL_FAILED'));
    return;
  }

  useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
  navigateToConversation(conversationId);
};

const startCall = async (inboxId, conversationIdHint = null) => {
  if (isCallButtonDisabled.value) return;

  const inbox = (inboxesList.value || []).find(i => i.id === inboxId);
  const provider = getVoiceCallProvider(inbox);
  if (provider === VOICE_CALL_PROVIDERS.WAVOIP) {
    const blockedReasonKey = wavoipOutboundBlockedReasonKey(inboxId);
    if (blockedReasonKey) {
      useAlert(t(blockedReasonKey));
      return;
    }
  }

  // Twilio tracks its own loading state via Vuex; gate the local ref only for
  // the browser-native providers so the spinner reflects the async init work.
  const trackLocally =
    provider === VOICE_CALL_PROVIDERS.WHATSAPP ||
    provider === VOICE_CALL_PROVIDERS.WAVOIP;
  if (trackLocally) isStartingCall.value = true;

  try {
    if (provider === VOICE_CALL_PROVIDERS.WHATSAPP) {
      await startWhatsappCall(inboxId, conversationIdHint);
      return;
    }
    if (provider === VOICE_CALL_PROVIDERS.WAVOIP) {
      await startWavoipCall(inboxId, conversationIdHint);
      return;
    }

    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.contactId,
      inboxId,
      conversationId: conversationIdHint,
    });
    const { call_sid: callSid, conversation_id: conversationId } = response;

    callsStore.addCall({
      callSid,
      conversationId,
      inboxId,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    });

    useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
    navigateToConversation(response?.conversation_id);
  } catch (error) {
    useAlert(error?.message || t('CONTACT_PANEL.CALL_FAILED'));
  } finally {
    if (trackLocally) isStartingCall.value = false;
  }
};

const onClick = async () => {
  // In conversation context, only stay in this conversation if its inbox is
  // itself voice-capable (works the same for Twilio and WhatsApp). For
  // non-voice channels (email, web, …) fall back to the picker so the call
  // goes out via a voice inbox.
  if (props.conversationId) {
    const conversation = store.getters.getConversationById(
      props.conversationId
    );
    const conversationInbox = (inboxesList.value || []).find(
      i => i.id === conversation?.inbox_id
    );
    if (conversationInbox && isVoiceCallEnabled(conversationInbox)) {
      await startCall(conversationInbox.id, props.conversationId);
      return;
    }
  }
  if (voiceInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }
  const [inbox] = voiceInboxes.value;
  await startCall(inbox.id);
};

const onPickInbox = async inbox => {
  dialogRef.value?.close();
  await startCall(inbox.id);
};

const inboxPickerIcon = inbox =>
  getInboxIconByType(inbox.channel_type || inbox.channelType, inbox.medium);
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :disabled="isCallButtonDisabled"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />

    <Dialog
      v-if="shouldRender && voiceInboxes.length > 1"
      ref="dialogRef"
      :title="$t('CONTACT_PANEL.VOICE_INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in voiceInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span
              :class="inboxPickerIcon(inbox)"
              class="size-4 text-n-slate-10"
            />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>
  </span>
</template>
